import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';

interface PagedResponse<T> {
  data: {
    items: T[];
    total: number;
  };
}

interface Organization {
  id: number;
}

interface User {
  id: number;
}

interface Project {
  id: number;
}

interface ScheduleItem {
  id: number;
  project_id: number;
  auditor_id: number;
  scheduled_start: string;
  scheduled_end: string;
  status: string;
}

const Dashboard: React.FC = () => {
  const [orgCount, setOrgCount] = useState<number | null>(null);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [projectCount, setProjectCount] = useState<number | null>(null);
  const [agenda, setAgenda] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [auditsToday, setAuditsToday] = useState<number | null>(null);
  const [auditsLast7, setAuditsLast7] = useState<number | null>(null);
  const [pdfsLast24h, setPdfsLast24h] = useState<number | null>(null);
  const [auditsSparkline, setAuditsSparkline] = useState<{ date: string; count: number }[]>([]);
  const [highRiskCount, setHighRiskCount] = useState<number | null>(null);
  const [highRiskSchedules, setHighRiskSchedules] = useState<
    {
      id: number;
      project_id: number;
      project_name: string | null;
      auditor_id: number;
      auditor_username: string | null;
      scheduled_date: string;
      status: string;
      priority: string;
      risk_score: number | null;
    }[]
  >([]);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<'all' | '7d' | '30d'>('all');
  const [leaderboard, setLeaderboard] = useState<
    {
      auditor_id: number;
      username: string;
      total_points: number;
      audits_completed: number;
      low_risk_audits: number;
      evidences_fixed: number;
    }[]
  >([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const [orgRes, userRes, projectRes, agendaRes, statsRes, highRiskRes, leaderboardRes] = await Promise.all([
          apiClient.get<PagedResponse<Organization>>('/organizations', { params: { per_page: 1 } }),
          apiClient.get<PagedResponse<User>>('/users', { params: { per_page: 1 } }),
          apiClient.get<PagedResponse<Project>>('/projects', { params: { per_page: 1 } }),
          apiClient.get('/schedules/my-agenda', { params: { per_page: 5 } }),
          apiClient.get<{
            data: {
              audits_completed_today: number;
              audits_completed_last_7_days: number;
              pdfs_generated_last_24h: number;
              audits_completed_last_7_days_by_date: { date: string; count: number }[];
              high_risk_schedules: number;
            };
          }>('/stats/overview'),
          apiClient.get<{
            data: {
              items: {
                id: number;
                project_id: number;
                project_name: string | null;
                auditor_id: number;
                auditor_username: string | null;
                scheduled_date: string;
                status: string;
                priority: string;
                risk_score: number | null;
              }[];
            };
          }>(
            '/schedules/high-risk',
          ),
          apiClient.get<{
            data: {
              items: {
                auditor_id: number;
                username: string;
                total_points: number;
                audits_completed: number;
                low_risk_audits: number;
                evidences_fixed: number;
              }[];
            };
          }>('/gamification/leaderboard', {
            params: { period: leaderboardPeriod },
          }),
        ]);

        setOrgCount(orgRes.data.data.total ?? orgRes.data.data.items.length);
        setUserCount(userRes.data.data.total ?? userRes.data.data.items.length);
        setProjectCount(projectRes.data.data.total ?? projectRes.data.data.items.length);
        setAgenda(agendaRes.data.data.items as ScheduleItem[]);

        const stats = statsRes.data.data;
        setAuditsToday(stats.audits_completed_today);
        setAuditsLast7(stats.audits_completed_last_7_days);
        setPdfsLast24h(stats.pdfs_generated_last_24h);
        setAuditsSparkline(stats.audits_completed_last_7_days_by_date || []);
        setHighRiskCount(stats.high_risk_schedules);

        setHighRiskSchedules(highRiskRes.data.data.items || []);
        setLeaderboard(leaderboardRes.data.data.items || []);
      } catch (e) {
        setError('No se pudieron cargar los datos del dashboard');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [leaderboardPeriod]);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        backgroundImage:
          'radial-gradient(circle at 0% 0%, rgba(56, 189, 248, 0.18), transparent 55%), radial-gradient(circle at 100% 0%, rgba(94, 234, 212, 0.18), transparent 55%)',
        padding: '24px 32px 32px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: 0.4,
              color: '#f9fafb',
            }}
          >
            Escritorio
          </h1>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 13,
              color: '#9ca3af',
            }}
          >
            Resumen en tiempo real de organizaciones, usuarios, proyectos y auditorías.
          </p>
        </div>
        <div
          style={{
            fontSize: 11,
            padding: '6px 10px',
            borderRadius: 999,
            border: '1px solid rgba(148, 163, 184, 0.45)',
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
          }}
        >
          Heimdall · {new Date().toLocaleDateString()}
        </div>
      </header>

      {loading && (
        <div
          style={{
            marginTop: 40,
            display: 'flex',
            justifyContent: 'center',
            color: '#9ca3af',
            fontSize: 14,
          }}
        >
          Cargando datos del dashboard...
        </div>
      )}

      {error && !loading && (
        <div
          style={{
            marginTop: 16,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(220, 38, 38, 0.12)',
            border: '1px solid rgba(248, 113, 113, 0.4)',
            color: '#fecaca',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* KPIs principales */}
          <section
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 16,
              marginBottom: 24,
            }}
          >
            <div
              style={{
                padding: 16,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #0f172a, #020617)',
                border: '1px solid rgba(148, 163, 184, 0.35)',
              }}
            >
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Organizaciones</div>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{orgCount ?? '-'}</div>
            </div>
            <div
              style={{
                padding: 16,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #0f172a, #020617)',
                border: '1px solid rgba(148, 163, 184, 0.35)',
              }}
            >
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Usuarios</div>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{userCount ?? '-'}</div>
            </div>
            <div
              style={{
                padding: 16,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #0f172a, #020617)',
                border: '1px solid rgba(148, 163, 184, 0.35)',
              }}
            >
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Proyectos</div>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{projectCount ?? '-'}</div>
            </div>
            <div
              style={{
                padding: 16,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #0f172a, #020617)',
                border: '1px solid rgba(248, 250, 252, 0.06)',
                boxShadow: '0 18px 40px rgba(56, 189, 248, 0.28)',
              }}
            >
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
                Auditorías completadas hoy
              </div>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{auditsToday ?? '-'}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                Últimos 7 días: <strong>{auditsLast7 ?? '-'}</strong>
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                PDFs últimas 24h: <strong>{pdfsLast24h ?? '-'}</strong>
              </div>
              <div style={{ fontSize: 11, color: '#f97316', marginTop: 2 }}>
                Auditorías de alto riesgo: <strong>{highRiskCount ?? '-'}</strong>
              </div>
            </div>
          </section>

          {/* Layout de 2 columnas principal */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.4fr)',
              gap: 20,
              alignItems: 'flex-start',
              marginBottom: 24,
            }}
          >
            {/* Columna izquierda: alto riesgo + agenda */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <section
                style={{
                  padding: 16,
                  borderRadius: 18,
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <h2
                    style={{
                      margin: 0,
                      fontSize: 15,
                      fontWeight: 600,
                      color: '#e5e7eb',
                    }}
                  >
                    Auditorías de alto riesgo
                  </h2>
                  <span style={{ fontSize: 11, color: '#f97316' }}>Prioridad máxima</span>
                </div>
                {highRiskSchedules.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
                    No hay auditorías de alto riesgo en este momento.
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            textAlign: 'left',
                            borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                          }}
                        >
                          <th style={{ padding: '6px 4px' }}>ID</th>
                          <th style={{ padding: '6px 4px' }}>Proyecto</th>
                          <th style={{ padding: '6px 4px' }}>Auditor</th>
                          <th style={{ padding: '6px 4px' }}>Fecha</th>
                          <th style={{ padding: '6px 4px' }}>Estado</th>
                          <th style={{ padding: '6px 4px' }}>Prioridad</th>
                          <th style={{ padding: '6px 4px' }}>Riesgo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {highRiskSchedules.map(item => (
                          <tr
                            key={item.id}
                            style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                          >
                            <td style={{ padding: '6px 4px' }}>
                              <a
                                href={`/agenda?scheduleId=${item.id}`}
                                style={{ color: '#38bdf8', textDecoration: 'none' }}
                              >
                                #{item.id}
                              </a>
                            </td>
                            <td style={{ padding: '6px 4px' }}>
                              {item.project_name ?? `#${item.project_id}`}
                            </td>
                            <td style={{ padding: '6px 4px' }}>
                              {item.auditor_username ?? `#${item.auditor_id}`}
                            </td>
                            <td style={{ padding: '6px 4px' }}>{item.scheduled_date}</td>
                            <td style={{ padding: '6px 4px' }}>{item.status}</td>
                            <td style={{ padding: '6px 4px' }}>{item.priority}</td>
                            <td style={{ padding: '6px 4px' }}>{item.risk_score ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section
                style={{
                  padding: 16,
                  borderRadius: 18,
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                }}
              >
                <h2
                  style={{
                    margin: '0 0 10px',
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#e5e7eb',
                  }}
                >
                  Próximas auditorías (mi agenda)
                </h2>
                {agenda.length === 0 && (
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
                    No hay auditorías próximas.
                  </p>
                )}
                {agenda.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            textAlign: 'left',
                            borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                          }}
                        >
                          <th style={{ padding: '6px 4px' }}>ID</th>
                          <th style={{ padding: '6px 4px' }}>Proyecto</th>
                          <th style={{ padding: '6px 4px' }}>Auditor</th>
                          <th style={{ padding: '6px 4px' }}>Inicio</th>
                          <th style={{ padding: '6px 4px' }}>Fin</th>
                          <th style={{ padding: '6px 4px' }}>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agenda.map(item => (
                          <tr
                            key={item.id}
                            style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                          >
                            <td style={{ padding: '6px 4px' }}>{item.id}</td>
                            <td style={{ padding: '6px 4px' }}>{item.project_id}</td>
                            <td style={{ padding: '6px 4px' }}>{item.auditor_id}</td>
                            <td style={{ padding: '6px 4px' }}>
                              {new Date(item.scheduled_start).toLocaleString()}
                            </td>
                            <td style={{ padding: '6px 4px' }}>
                              {new Date(item.scheduled_end).toLocaleString()}
                            </td>
                            <td style={{ padding: '6px 4px' }}>{item.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>

            {/* Columna derecha: ranking + actividad reciente */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <section
                style={{
                  padding: 16,
                  borderRadius: 18,
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <h2
                    style={{
                      margin: 0,
                      fontSize: 15,
                      fontWeight: 600,
                      color: '#e5e7eb',
                    }}
                  >
                    Ranking de auditores (gamificación)
                  </h2>
                  <div style={{ fontSize: 11 }}>
                    <span style={{ marginRight: 4 }}>Periodo:</span>
                    <select
                      value={leaderboardPeriod}
                      onChange={e =>
                        setLeaderboardPeriod(e.target.value as 'all' | '7d' | '30d')
                      }
                      style={{
                        fontSize: 11,
                        backgroundColor: '#020617',
                        color: '#e5e7eb',
                        borderRadius: 999,
                        border: '1px solid rgba(148, 163, 184, 0.6)',
                        padding: '4px 8px',
                      }}
                    >
                      <option value="all">Todos</option>
                      <option value="7d">Últimos 7 días</option>
                      <option value="30d">Últimos 30 días</option>
                    </select>
                  </div>
                </div>
                {leaderboard.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
                    Aún no hay puntos registrados.
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            textAlign: 'left',
                            borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                          }}
                        >
                          <th style={{ padding: '6px 4px' }}>#</th>
                          <th style={{ padding: '6px 4px' }}>Auditor</th>
                          <th style={{ padding: '6px 4px' }}>Puntos</th>
                          <th style={{ padding: '6px 4px' }}>Completadas</th>
                          <th style={{ padding: '6px 4px' }}>Bajo riesgo</th>
                          <th style={{ padding: '6px 4px' }}>Evidencias corregidas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaderboard.map((row, index) => (
                          <tr
                            key={row.auditor_id}
                            style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                          >
                            <td style={{ padding: '6px 4px' }}>{index + 1}</td>
                            <td style={{ padding: '6px 4px' }}>{row.username}</td>
                            <td style={{ padding: '6px 4px' }}>{row.total_points}</td>
                            <td style={{ padding: '6px 4px' }}>{row.audits_completed}</td>
                            <td style={{ padding: '6px 4px' }}>{row.low_risk_audits}</td>
                            <td style={{ padding: '6px 4px' }}>{row.evidences_fixed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section
                style={{
                  padding: 16,
                  borderRadius: 18,
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                }}
              >
                <h2
                  style={{
                    margin: '0 0 10px',
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#e5e7eb',
                  }}
                >
                  Actividad reciente
                </h2>
                {auditsToday == null && auditsLast7 == null && pdfsLast24h == null ? (
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
                    No hay datos de actividad todavía.
                  </p>
                ) : (
                  <div style={{ maxWidth: 420 }}>
                    {auditsToday != null && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, marginBottom: 2 }}>
                          Auditorías completadas hoy: {auditsToday}
                        </div>
                        <div
                          style={{
                            background: 'rgba(31, 41, 55, 0.9)',
                            height: 10,
                            borderRadius: 999,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(100, auditsToday * 10)}%`,
                              height: '100%',
                              background:
                                'linear-gradient(90deg, #22c55e, #16a34a, #22c55e)',
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {auditsLast7 != null && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, marginBottom: 2 }}>
                          Auditorías completadas últimos 7 días: {auditsLast7}
                        </div>
                        <div
                          style={{
                            background: 'rgba(31, 41, 55, 0.9)',
                            height: 10,
                            borderRadius: 999,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(100, auditsLast7 * 5)}%`,
                              height: '100%',
                              background:
                                'linear-gradient(90deg, #38bdf8, #0ea5e9, #38bdf8)',
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {pdfsLast24h != null && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, marginBottom: 2 }}>
                          PDFs generados últimas 24h: {pdfsLast24h}
                        </div>
                        <div
                          style={{
                            background: 'rgba(31, 41, 55, 0.9)',
                            height: 10,
                            borderRadius: 999,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(100, pdfsLast24h * 10)}%`,
                              height: '100%',
                              background:
                                'linear-gradient(90deg, #f97316, #ea580c, #f97316)',
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {auditsSparkline.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>
                          Completadas por día (últimos 7 días)
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 4,
                            height: 60,
                            paddingBottom: 4,
                            borderBottom: '1px solid rgba(55, 65, 81, 0.9)',
                          }}
                        >
                          {auditsSparkline.map(point => {
                            const maxCount = Math.max(
                              1,
                              ...auditsSparkline.map(p => p.count),
                            );
                            const heightPct = (point.count / maxCount) * 100;
                            return (
                              <div
                                key={point.date}
                                style={{
                                  width: 12,
                                  background:
                                    'linear-gradient(180deg, #38bdf8, #0ea5e9)',
                                  height: `${heightPct}%`,
                                }}
                                title={`${point.date}: ${point.count}`}
                              />
                            );
                          })}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 10,
                            marginTop: 2,
                          }}
                        >
                          {auditsSparkline.map(point => (
                            <span key={point.date}>{point.date.slice(5)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
